import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';

import Close from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { Chip, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';

import useHistoryOfChangesContext from 'pages/item/v2/hooks/use-history-of-changes-context';
import { HistoryOfChangesItem } from 'pages/item/v2/types/history-of-changes';
import isEmpty from 'utils/is-empty';

import MilestoneIcon from './assets/milestone.svg';
import useStyles from './styles';

interface Props {
    published?: Date;
    className?: string;
}

const RevisionSelect: React.FC<Props> = ({ published, className }) => {
    const { classes, cx } = useStyles();

    const { data, revision, setVersion, isLoading, load } = useHistoryOfChangesContext();
    const date = revision?.published ?? published ?? null;

    const groups = useMemo(() => {
        const groups = new Map<string, HistoryOfChangesItem[]>();
        data.forEach((item) => {
            const key = format(item.published, 'LLL dd, yyyy');
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)?.push(item);
        });

        return [...groups.entries()];
    }, [data]);

    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const handleOpen = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            load();
            setAnchor(event.currentTarget);
        },
        [load]
    );
    const handleClose = useCallback(() => setAnchor(null), []);

    return (
        <>
            <Chip
                className={cx(classes.chip, className)}
                icon={<HistoryIcon className={classes.icon} />}
                variant="outlined"
                clickable
                onClick={handleOpen}
                label={
                    <div className={classes.label}>
                        <span className={classes.text}>{'History'}</span>
                        {date ? <span className={classes.date}>{format(date, 'LLL dd, yyyy - h:mm aaaa')}</span> : null}
                    </div>
                }
            />
            <Menu
                classes={{ paper: classes.menu, list: classes.menuList }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                anchorEl={anchor}
                open={!!anchor}
                onClose={handleClose}
            >
                <div className={classes.menuTitle}>
                    <span>{'Version history'}</span>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </div>
                {isLoading ? (
                    <div className={classes.spinner}>
                        <CircularProgress size={24} />
                    </div>
                ) : isEmpty(groups) ? (
                    <MenuItem disabled className={classes.empty}>
                        {'Nothing found'}
                    </MenuItem>
                ) : (
                    groups.map(([name, items], index) => [
                        <div key={name} className={cx(classes.milestone, { [classes.first]: index === 0 })}>
                            <MilestoneIcon />
                            <span>{name}</span>
                        </div>,
                        ...items.map((item) => (
                            <MenuItem
                                key={item.version}
                                onClick={() => {
                                    setVersion(item.version);
                                    handleClose();
                                }}
                                className={classes.menuItem}
                                disableRipple
                            >
                                <div
                                    className={cx(classes.version, {
                                        [classes.selected]: item.version === revision?.version
                                    })}
                                >
                                    <span>{`Version ${item.version}`}</span>
                                    {item.version === data[0].version ? (
                                        <Chip
                                            label={'Current'}
                                            classes={{ root: classes.current, label: classes.label }}
                                            variant="outlined"
                                            size="small"
                                        />
                                    ) : null}
                                </div>
                                <div className={classes.description}>
                                    <span>{'Updated fields: '}</span>
                                    <span className={classes.text}>{Object.keys(item.changes).join(', ')}</span>
                                    <span className={classes.date}>{format(item.published, 'h:mm aaaa')}</span>
                                </div>
                            </MenuItem>
                        ))
                    ])
                )}
            </Menu>
        </>
    );
};

export default RevisionSelect;
